import {
    ref, computed, watch, Ref
} from 'vue';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { fetchBacklogMetricsByBuckets } from '@/pages/OperationsInsights/services/fetchBacklogMetricsByBuckets';
import {
    ApiRespBacklogMetricsByBuckets,
    BacklogMetricsByBucket
} from '@/pages/OperationsInsights/types/BacklogMetricsResponse';
import { getLabelByInterval } from '@/pages/OperationsInsights/utils/getLabelByInterval';
import DateCellRenderer from '@/pages/agGridUtils/DateCellRenderer.vue';
import OpenBacklogCellRenderer from '@/pages/agGridUtils/OpenBacklogCellRenderer.vue';
import { tfCsBLInOutByInterval } from '@/pages/OperationsInsights/composables/transformations/tfCsBLInOutByInterval';
import {
    BacklogSummariesComponentData,
    tfBLSummariesData
} from '@/pages/OperationsInsights/composables/transformations/tfBLSummariesData';
import { useBacklogConfigurationsStore } from '@/stores/backlogConfigurationsStore';
const { ageBandConfigNamesStore } = useBacklogConfigurationsStore();
export function useBLMetricsData(queryParams: Ref<ApiQueryParams>) {
    const logger = useLogger('useBLMetricsData');

    const backlogMetricsData = ref<ApiRespBacklogMetricsByBuckets | null>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const interval = computed(() => {
        if (backlogMetricsData.value?.aggregationPeriod.interval) {
            return getLabelByInterval(backlogMetricsData.value?.aggregationPeriod.interval);
        } else {
            return '';
        }
    });

    // Build summary data
    const backlogSummariesData = computed(() => {
        if (!backlogMetricsData.value) return {} as BacklogSummariesComponentData;
        return tfBLSummariesData(backlogMetricsData.value);
    });

    // High Chart - Backlog in / out / totalOpen by interval
    const backlogMetricsChartSeries = computed(() => {
        if (!backlogMetricsData.value) return [];
        return tfCsBLInOutByInterval(backlogMetricsData.value);
    });

    // Helper function to transform backlog metrics by interval to grid data
    const transformBacklogMetricsToGridData = (metrics: BacklogMetricsByBucket[]) => {
        const gridMap = new Map<Date, object>();
        for (const backlogMetric of metrics) {
            if (gridMap.has(backlogMetric.bucket)) {
                logger.error('Duplicated buckets', { backlogMetric });
            } else {
                const row = {
                    bucket: new Date(backlogMetric.bucket),
                    createdCount: backlogMetric.createdCount ?? 0,
                    closedWithinSLA: backlogMetric.closedWithinSLA ?? 0,
                    closedBreached: backlogMetric.closedBreached ?? 0,
                    openCount: backlogMetric.openCount ?? 0,
                    openCountChanges: backlogMetric.openCountChanges ?? 0
                };
                gridMap.set(backlogMetric.bucket, row);
            }
        }
        return Array.from(gridMap.values());
    };

    // Grid Data and Colum Def for (total backlog by interval)
    const backlogTotalByIntervalGridData = computed(() => {
        if (!backlogMetricsData.value) return [];
        return transformBacklogMetricsToGridData(backlogMetricsData.value.metrics);
    });

    const backlogTotalByIntervalColumnDefs = computed(() => {
        if (!backlogMetricsData.value) return [];
        const interval = getLabelByInterval(backlogMetricsData.value.aggregationPeriod.interval);
        return [
            {
                field: 'bucket',
                headerName: interval,
                flex: 2,
                filter: true,
                cellRenderer: DateCellRenderer // Reference the registered component by name
            },
            {
                field: 'createdCount', headerName: 'Created', filter: true, flex: 1
            },
            {
                field: 'closedWithinSLA', headerName: 'Completed in SLA',  filter: true, flex: 1
            },
            {
                field: 'closedBreached', headerName: 'Completed Breached', filter: true, flex: 1
            },
            {
                field: 'openCount', headerName: 'Total Backlog', filter: true, cellRenderer: OpenBacklogCellRenderer, flex: 1
            }
            // { field: 'openCountChanges', headerName: 'Backlog Changes', maxWidth: 140, filter: true }
        ];
    });

    let ageBandConfigNamesByDuration: string[] = [];

    // Fetch Data
    const fetchData = async () => {
        if (!ageBandConfigNamesByDuration.length) {
            ageBandConfigNamesByDuration = ageBandConfigNamesStore.data;
        }

        if (!queryParams.value) return;
        if (!queryParams.value.workstreamDefinitionIds?.length) return;

        isLoading.value = true;
        error.value = null;

        try {
            const [backlogMetricsByBucket] = await Promise.all([fetchBacklogMetricsByBuckets(queryParams.value)]);

            backlogMetricsData.value = backlogMetricsByBucket;
        } catch (err) {
            error.value = 'Failed to fetch backlog data';
            logger.error('Failed to fetch backlog data', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    // Watch queryParams and fetch data when they change
    watch(queryParams, fetchData, { immediate: true, deep: true });

    return {
        interval,
        backlogSummariesData,
        backlogMetricsChartSeries,
        backlogTotalByIntervalGridData,
        backlogTotalByIntervalColumnDefs,
        isLoading,
        error,
        fetchData
    };
}